/**
 * Hooks de captura de avance (Fase 3).
 *
 * - `useAvanceSprint`  → lee las sub-partidas del sprint con su avance + peso.
 *   Devuelve `null` cuando la obra aún no está habilitada (404 del backend).
 * - `useRegistrarAvance` → registra avance de una sub-partida, con soporte
 *   offline: si no hay conexión (o la red falla) la mutación se encola en
 *   IndexedDB y se aplica un update optimista al cache.
 * - `useSincronizarAvances` → drena la cola al recuperar conexión.
 *
 * Habilitar/configurar una obra (tipo_casa + sprint inicial) vive en admin:
 * ver `useHabilitarObra` en hooks/useObras.ts.
 */

import { ErrorHttp, cliente } from '@/lib/cliente';
import { type Despachador, type TipoMutacion, drenar, enqueue } from '@/lib/queueOffline';
import type { AvanceSprint, RegistrarAvance } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

/** Key del cache. `sprint` indefinido = "sprint actual de la obra". */
function avanceKey(codigo: string | undefined, sprint?: number) {
  return ['avance', codigo, sprint ?? null] as const;
}

/**
 * Lee el avance del sprint de una obra. `null` = obra no iniciada todavía.
 */
export function useAvanceSprint(codigo: string | undefined, sprint?: number) {
  return useQuery({
    queryKey: avanceKey(codigo, sprint),
    enabled: !!codigo,
    // Si el lote ya sembró este caché, no se vuelve a pedir por 60s → apertura
    // instantánea. El registrar invalida con refetchType:'none', así que el
    // optimista manda.
    staleTime: 60_000,
    queryFn: async (): Promise<AvanceSprint | null> => {
      const r = await cliente.obras.avance(codigo as string, sprint).catch((e) => {
        if (e instanceof ErrorHttp && e.status === 404) return null; // no iniciada
        throw e;
      });
      if (r === null) return null;
      return r.error === null ? r.data : null;
    },
  });
}

/**
 * Pre-carga el avance COMPLETO de todas las obras del proyecto en un solo
 * request y SIEMBRA el caché por-obra, para que abrir cualquier obra sea
 * instantáneo (réplica del "loadProyectoData" de la app vieja). Montar en el
 * contenedor del tablero de campo con el proyecto seleccionado.
 */
export function useAvanceLote(proyecto: string | null) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['avance-lote', proyecto],
    queryFn: () => cliente.obras.avanceLote(proyecto),
    staleTime: 60_000,
  });
  useEffect(() => {
    const res = q.data;
    if (res && res.error === null && res.data) {
      for (const [codigo, avance] of Object.entries(res.data)) {
        const key = avanceKey(codigo, undefined);
        // Sembrar SOLO si la obra no está ya en caché: nunca pisar un update
        // optimista (o un fetch propio más fresco) con el dato del lote. La obra
        // se auto-actualiza al re-abrirla (su query queda stale).
        if (qc.getQueryData(key) === undefined) qc.setQueryData(key, avance);
      }
    }
  }, [q.data, qc]);
  return q;
}

/** Aplica un avance PARCIAL a una copia del estado y recalcula avance_sprint. */
function aplicarOptimista(old: AvanceSprint | null | undefined, body: RegistrarAvance) {
  if (!old) return old;
  const tocaPct = body.pct_completado !== undefined || body.completada !== undefined;
  const tocaNc = body.nc_causa !== undefined;
  const sub_partidas = old.sub_partidas.map((sp) => {
    if (sp.sub_partida_id !== body.sub_partida_id) return sp;
    const next = { ...sp };
    if (tocaPct) {
      next.pct_completado = body.completada ? 100 : (body.pct_completado ?? sp.pct_completado);
      next.completada = body.completada === true || next.pct_completado >= 100;
    }
    if (tocaNc) {
      next.nc_causa = body.nc_causa ?? null;
      next.nc_nota = body.nc_nota ?? null;
    }
    return next;
  });
  const avance_sprint =
    Math.round(
      sub_partidas
        .filter((sp) => !sp.arrastrada)
        .reduce((acc, sp) => acc + (sp.pct_completado * sp.peso) / 100, 0) * 100,
    ) / 100;
  return { ...old, sub_partidas, avance_sprint };
}

/**
 * Registra avance de una sub-partida. Optimista + offline-first:
 *   - online OK → escribe al backend
 *   - sin conexión / error de red → encola en IndexedDB (se sincroniza luego)
 *   - error del servidor (4xx/5xx) → revierte el optimista y propaga el error
 */
export function useRegistrarAvance(codigo: string, sprint?: number) {
  const qc = useQueryClient();
  const key = avanceKey(codigo, sprint);

  return useMutation({
    mutationFn: async (body: RegistrarAvance): Promise<{ encolado: boolean }> => {
      if (!navigator.onLine) {
        await enqueue('avance.registrar', { codigo, body });
        return { encolado: true };
      }
      try {
        await cliente.obras.registrarAvance(codigo, body);
        return { encolado: false };
      } catch (e) {
        // Error de red (no hubo respuesta HTTP) → encolar como respaldo.
        if (!(e instanceof ErrorHttp)) {
          await enqueue('avance.registrar', { codigo, body });
          return { encolado: true };
        }
        throw e;
      }
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<AvanceSprint | null>(key);
      qc.setQueryData<AvanceSprint | null>(key, (old) => aplicarOptimista(old, body));
      return { prev };
    },
    onError: (_e, _body, ctx) => {
      if (ctx) qc.setQueryData(key, ctx.prev);
    },
    onSettled: (res) => {
      // El update ya es OPTIMISTA y correcto; marcamos stale SIN refetch
      // inmediato (refetchType: 'none') para no traer datos en cada toque.
      // Se re-sincroniza al volver a montar cada vista (refetchOnMount).
      if (res && !res.encolado && navigator.onLine) {
        qc.invalidateQueries({ queryKey: key, refetchType: 'none' });
        // Las vistas del tablero están inactivas mientras se captura: marcarlas
        // stale para que reflejen el cambio AL VOLVER (refetch on mount), sin
        // disparar requests ahora.
        qc.invalidateQueries({ queryKey: ['matriz'], refetchType: 'none' });
        qc.invalidateQueries({ queryKey: ['cierre'], refetchType: 'none' });
      }
    },
  });
}

/**
 * Marca como completadas las sub-partidas arrastradas (de sprints anteriores)
 * de una obra. Atajo para no marcarlas una por una. Refresca el avance de la
 * obra y marca stale el tablero.
 */
export function useCompletarArrastradas(codigo: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cliente.obras.completarArrastradas(codigo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: avanceKey(codigo, undefined) });
      qc.invalidateQueries({ queryKey: ['cierre'], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['matriz'], refetchType: 'none' });
    },
  });
}

/** Despachadores de la cola offline para los tipos de mutación de avance. */
const dispatchers: Partial<Record<TipoMutacion, Despachador>> = {
  'avance.registrar': async (m) => {
    const { codigo, body } = m.payload as { codigo: string; body: RegistrarAvance };
    await cliente.obras.registrarAvance(codigo, body);
  },
};

/**
 * Drena la cola offline al recuperar conexión (y una vez al montar).
 * Montar este hook una sola vez (en el layout de campo).
 */
export function useSincronizarAvances() {
  const qc = useQueryClient();
  useEffect(() => {
    let activo = true;
    const sync = async () => {
      if (!navigator.onLine) return;
      const r = await drenar(dispatchers).catch(() => ({ ok: 0, fallidas: 0 }));
      if (!activo) return;
      if (r.ok > 0) {
        qc.invalidateQueries({ queryKey: ['avance'] });
        toast.success(
          `${r.ok} avance${r.ok === 1 ? '' : 's'} sincronizado${r.ok === 1 ? '' : 's'}`,
        );
      }
    };
    window.addEventListener('online', sync);
    sync();
    return () => {
      activo = false;
      window.removeEventListener('online', sync);
    };
  }, [qc]);
}
